import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Warning, ClockCounterClockwise, CaretDown, CaretRight, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import api from "../lib/api";
import NumberInput from "@/components/ui/NumberInput";

/**
 * Panel unificado para Tienda Nube:
 *  1) Config del auto-sync stock → TN (input minutos + Guardar).
 *  2) Alerta SKU faltante (variantes TN sin SKU / SKU sin match local).
 *  3) Historial de sync (últimas 10 corridas, con detalles de fallos).
 *
 * Se monta tanto en /tienda-nube como en /stock para que el operador de
 * stock pueda pausar el auto-sync y ver fallas sin ir a la página TN.
 *
 * Se auto-esconde silenciosamente si TN no responde a `GET /tiendanube/auto-sync`
 * (ej. usuario sin permisos o TN desconectado).
 */
export default function TiendaNubeSyncPanels({ testidPrefix = "tn" }) {
  // Auto-sync config
  const [autoSyncMin, setAutoSyncMin] = useState(15);
  const [savingAutoSync, setSavingAutoSync] = useState(false);
  const [lastSync, setLastSync] = useState({});
  const [pushInfo, setPushInfo] = useState({ enabled: true, preview: false });
  const [tnAvailable, setTnAvailable] = useState(null); // null | true | false

  // Historial
  const [syncHistory, setSyncHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState(null);
  // Tab del historial: "stock" (default) o "precios"
  const [historyTab, setHistoryTab] = useState("stock");

  // SKU faltantes
  const [skuFaltantes, setSkuFaltantes] = useState({
    sin_sku: [], sin_link: [], totales: { sin_sku: 0, sin_link: 0 },
  });
  const [loadingSku, setLoadingSku] = useState(false);
  const [skuOpen, setSkuOpen] = useState(false);
  const [skuTab, setSkuTab] = useState("sin_sku");

  const loadAutoSync = useCallback(async () => {
    try {
      const { data } = await api.get("/tiendanube/auto-sync");
      setAutoSyncMin(data.auto_stock_sync_minutes ?? 15);
      setLastSync({
        at: data.last_stock_sync_at,
        source: data.last_stock_sync_source,
        summary: data.last_stock_sync_summary,
      });
      setPushInfo({ enabled: data.push_enabled !== false, preview: !!data.is_preview_env });
      setTnAvailable(true);
    } catch (_) {
      setTnAvailable(false);
    }
  }, []);

  const loadSyncHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/tiendanube/sync-history", {
        params: { source: historyTab },
      });
      setSyncHistory(data?.runs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el historial de sync");
    } finally {
      setLoadingHistory(false);
    }
  }, [historyTab]);

  const loadSkuFaltantes = useCallback(async () => {
    setLoadingSku(true);
    try {
      const { data } = await api.get("/tiendanube/sku-faltantes");
      setSkuFaltantes(data || { sin_sku: [], sin_link: [], totales: { sin_sku: 0, sin_link: 0 } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la alerta de SKU");
    } finally {
      setLoadingSku(false);
    }
  }, []);

  useEffect(() => {
    loadAutoSync();
  }, [loadAutoSync]);

  useEffect(() => {
    if (tnAvailable) {
      loadSyncHistory();
      loadSkuFaltantes();
    }
  }, [tnAvailable, loadSyncHistory, loadSkuFaltantes]);

  const handleSaveAutoSync = async () => {
    const n = parseInt(autoSyncMin, 10);
    if (isNaN(n) || n < 0 || n > 1440) return toast.warning("Entre 0 y 1440 minutos");
    setSavingAutoSync(true);
    try {
      await api.put("/tiendanube/auto-sync", { auto_stock_sync_minutes: n });
      toast.success(n > 0 ? `Auto-sync cada ${n} minutos activado` : "Auto-sync desactivado");
      loadAutoSync();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSavingAutoSync(false);
    }
  };

  if (tnAvailable === false) return null; // TN no disponible, ocultamos el bloque

  const totalSku = (skuFaltantes.totales?.sin_sku || 0) + (skuFaltantes.totales?.sin_link || 0);
  const hayFaltantes = totalSku > 0;

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-sync-panels`}>
      {!pushInfo.enabled && (
        <div className="border border-red-300 bg-red-50 rounded-sm px-3 py-2 text-[12px] text-red-800 flex items-center gap-2" data-testid={`${testidPrefix}-push-disabled-banner`}>
          <span className="font-mono uppercase tracking-widest text-[10px] font-bold">Push a TN deshabilitado</span>
          <span>
            Este entorno {pushInfo.preview ? "es el de PRUEBA (preview)" : "tiene el push desactivado"}: no escribe stock ni precios en la tienda para no pisar los datos reales.
            {pushInfo.preview && " El sistema productivo es el que sincroniza."} Se puede cambiar en Tienda Nube › Configuración.
          </span>
        </div>
      )}
      {/* Auto-sync config */}
      <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-3" data-testid={`${testidPrefix}-auto-sync-card`}>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[11px] uppercase font-mono text-emerald-700 tracking-widest">
            Auto-sync stock → TN
          </div>
          <NumberInput raw min="0" max="1440" step="1"
            value={autoSyncMin}
            onChange={(e) => setAutoSyncMin(e.target.value)}
            className="w-20 h-8 rounded border border-emerald-300 px-2 text-sm font-mono text-right"
            data-testid={`${testidPrefix}-input-auto-sync-min`}
          />
          <span className="text-xs text-emerald-800">min entre pushes</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleSaveAutoSync}
            disabled={savingAutoSync}
            className="border-emerald-400 text-emerald-800 hover:bg-emerald-100 text-xs h-8"
            data-testid={`${testidPrefix}-btn-save-auto-sync`}
          >
            {savingAutoSync ? "..." : "Guardar"}
          </Button>
          <div className="text-[11px] text-emerald-800 font-mono ml-auto">
            {parseInt(autoSyncMin, 10) > 0 ? `Cada ${autoSyncMin} min` : "Auto-sync desactivado (0 = manual)"}
            {lastSync?.at && <> · último {(lastSync.at || "").slice(0, 16).replace("T", " ")} ({lastSync.source})</>}
            {lastSync?.summary?.total > 0 && (
              <> · {lastSync.summary.sincronizados}✓{lastSync.summary.sin_cambio ? ` / ${lastSync.summary.sin_cambio} sin cambio` : ""} / {lastSync.summary.saltados}sin match / {lastSync.summary.fallidos}✗</>
            )}
          </div>
        </div>
      </div>

      {/* Alerta SKU faltante */}
      <div
        className={`border rounded-sm ${hayFaltantes ? "border-amber-300 bg-amber-50/60" : "border-neutral-200 bg-neutral-50/60"}`}
        data-testid={`${testidPrefix}-sku-faltantes-card`}
      >
        <button
          type="button"
          onClick={() => setSkuOpen((v) => !v)}
          className="w-full flex items-center gap-2 p-3 text-left"
          data-testid={`${testidPrefix}-btn-toggle-sku-faltantes`}
        >
          {skuOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          <Warning size={16} weight="fill" className={hayFaltantes ? "text-amber-600" : "text-neutral-400"} />
          <span className={`text-[11px] uppercase font-mono tracking-widest ${hayFaltantes ? "text-amber-800" : "text-neutral-600"}`}>
            Alerta SKU faltante
          </span>
          <span className={`text-xs font-mono ${hayFaltantes ? "text-amber-900" : "text-neutral-500"}`}>
            {hayFaltantes
              ? `${skuFaltantes.totales.sin_sku} sin SKU · ${skuFaltantes.totales.sin_link} sin match local`
              : "Sin faltantes"}
          </span>
          <Button
            size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); loadSkuFaltantes(); }}
            disabled={loadingSku}
            className="ml-auto text-xs h-7 gap-1"
            data-testid={`${testidPrefix}-btn-refresh-sku-faltantes`}
          >
            <ArrowsClockwise size={12} weight="bold" className={loadingSku ? "animate-spin" : ""} />
            Refrescar
          </Button>
        </button>
        {skuOpen && (
          <div className="border-t border-amber-200 p-3">
            <div className="flex gap-2 mb-2">
              <Button
                size="sm" variant={skuTab === "sin_sku" ? "default" : "outline"}
                onClick={() => setSkuTab("sin_sku")}
                className="text-xs h-7"
                data-testid={`${testidPrefix}-tab-sin-sku`}
              >
                Sin SKU en TN ({skuFaltantes.totales?.sin_sku || 0})
              </Button>
              <Button
                size="sm" variant={skuTab === "sin_link" ? "default" : "outline"}
                onClick={() => setSkuTab("sin_link")}
                className="text-xs h-7"
                data-testid={`${testidPrefix}-tab-sin-link`}
              >
                SKU sin match local ({skuFaltantes.totales?.sin_link || 0})
              </Button>
            </div>
            <div className="text-[11px] text-neutral-600 mb-2 font-mono">
              {skuTab === "sin_sku"
                ? "Variantes de Tienda Nube sin código SKU cargado. Cargá el SKU en TN para que la sincronización pueda matchearlas."
                : "Variantes de Tienda Nube con SKU cargado, pero ese código NO existe en el catálogo local (Productos). Creá el producto o corregí el código."}
            </div>
            <div className="max-h-72 overflow-auto border border-amber-200 bg-white">
              <table className="w-full text-xs font-mono">
                <thead className="bg-amber-100 text-amber-900 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Producto TN</th>
                    <th className="text-left px-2 py-1 w-32">SKU</th>
                    <th className="text-right px-2 py-1 w-20">Stock TN</th>
                  </tr>
                </thead>
                <tbody>
                  {(skuFaltantes[skuTab] || []).length === 0 ? (
                    <tr><td colSpan={3} className="text-center py-4 text-neutral-500">— sin registros —</td></tr>
                  ) : (
                    (skuFaltantes[skuTab] || []).map((v) => (
                      <tr key={`${v.tn_product_id}-${v.tn_variant_id}`} className="border-t border-neutral-100">
                        <td className="px-2 py-1">{v.nombre || "—"}</td>
                        <td className="px-2 py-1">{v.sku || <span className="text-red-600">SIN SKU</span>}</td>
                        <td className="px-2 py-1 text-right">{v.stock ?? "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Historial de sync */}
      <div className="border border-blue-200 bg-blue-50/40 rounded-sm" data-testid={`${testidPrefix}-sync-history-card`}>
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="w-full flex items-center gap-2 p-3 text-left"
          data-testid={`${testidPrefix}-btn-toggle-sync-history`}
        >
          {historyOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
          <ClockCounterClockwise size={16} weight="bold" className="text-blue-700" />
          <span className="text-[11px] uppercase font-mono tracking-widest text-blue-800">
            Historial de sync (últimas 10)
          </span>
          <span className="text-xs font-mono text-blue-900">
            {syncHistory.length} corridas
          </span>
          <Button
            size="sm" variant="outline"
            onClick={(e) => { e.stopPropagation(); loadSyncHistory(); }}
            disabled={loadingHistory}
            className="ml-auto text-xs h-7 gap-1 border-blue-400 text-blue-800 hover:bg-blue-100"
            data-testid={`${testidPrefix}-btn-refresh-sync-history`}
          >
            <ArrowsClockwise size={12} weight="bold" className={loadingHistory ? "animate-spin" : ""} />
            Refrescar
          </Button>
        </button>
        {historyOpen && (
          <div className="border-t border-blue-200 p-3">
            {/* Tabs de tipo de sync */}
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => setHistoryTab("stock")}
                className={`px-3 py-1 text-[11px] font-mono uppercase rounded-sm ${
                  historyTab === "stock" ? "bg-emerald-600 text-white" : "bg-neutral-100 hover:bg-neutral-200"
                }`}
                data-testid={`${testidPrefix}-history-tab-stock`}
              >Stock</button>
              <button
                onClick={() => setHistoryTab("precios")}
                className={`px-3 py-1 text-[11px] font-mono uppercase rounded-sm ${
                  historyTab === "precios" ? "bg-sky-600 text-white" : "bg-neutral-100 hover:bg-neutral-200"
                }`}
                data-testid={`${testidPrefix}-history-tab-precios`}
              >Precios</button>
              <span className="ml-auto text-[10px] font-mono text-neutral-500">
                {syncHistory.length} corridas · rolling últimas 10 por tipo
              </span>
            </div>
            {syncHistory.length === 0 ? (
              <div className="text-center py-4 text-neutral-500 text-xs">
                — aún no hay corridas de {historyTab === "precios" ? "precios" : "stock"} —
              </div>
            ) : (
              <div className="border border-blue-200 bg-white">
                <table className="w-full text-xs font-mono">
                  <thead className="bg-blue-100 text-blue-900">
                    <tr>
                      <th className="w-6 px-1 py-1"></th>
                      <th className="text-left px-2 py-1">Fecha</th>
                      <th className="text-left px-2 py-1 w-20">Origen</th>
                      <th className="text-right px-2 py-1 w-16">Total</th>
                      <th className="text-right px-2 py-1 w-16">{historyTab === "precios" ? "Aplicados" : "OK"}</th>
                      {historyTab !== "precios" && (
                        <th className="text-right px-2 py-1 w-20">Sin match</th>
                      )}
                      <th className="text-right px-2 py-1 w-16">Fallidos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncHistory.map((r) => {
                      const isOpen = expandedRunId === r.id;
                      const s = r.summary || {};
                      const esPrecios = r.source === "precios";
                      const fallos = (r.detalles || []).filter((d) => d.error);
                      // KPIs por tipo
                      const total = esPrecios ? (s.total ?? 0) : (s.total ?? 0);
                      const okCount = esPrecios ? (s.aplicados ?? 0) : (s.sincronizados ?? 0);
                      const failCount = s.fallidos ?? 0;
                      const saltados = s.saltados ?? 0;
                      return (
                        <Fragment key={r.id}>
                          <tr
                            className="border-t border-neutral-100 cursor-pointer hover:bg-blue-50/60"
                            onClick={() => setExpandedRunId(isOpen ? null : r.id)}
                            data-testid={`${testidPrefix}-sync-run-row-${r.id}`}
                          >
                            <td className="px-1 py-1 text-center">
                              {isOpen ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                            </td>
                            <td className="px-2 py-1">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
                            <td className="px-2 py-1">
                              <Badge variant="outline" className={
                                esPrecios ? "text-sky-700 border-sky-300"
                                : r.source === "manual" ? "text-purple-700 border-purple-300"
                                : "text-emerald-700 border-emerald-300"
                              }>
                                {esPrecios ? "precios" : r.source}
                              </Badge>
                            </td>
                            <td className="px-2 py-1 text-right">{total}</td>
                            <td className="px-2 py-1 text-right text-emerald-700">{okCount}</td>
                            {historyTab !== "precios" && (
                              <td className="px-2 py-1 text-right text-neutral-500">{saltados}</td>
                            )}
                            <td className={`px-2 py-1 text-right font-bold ${failCount > 0 ? "text-red-600" : "text-neutral-500"}`}>{failCount}</td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-blue-50/40">
                              <td colSpan={historyTab === "precios" ? 6 : 7} className="p-2">
                                {esPrecios ? (
                                  <PrecioRunDetail run={r} fallos={fallos} />
                                ) : fallos.length > 0 ? (
                                  <>
                                    <div className="text-[11px] font-bold text-red-700 mb-1">Fallos ({fallos.length}):</div>
                                    <div className="max-h-40 overflow-auto border border-red-200 bg-white">
                                      <table className="w-full text-[11px]">
                                        <thead className="bg-red-100 text-red-900">
                                          <tr>
                                            <th className="text-left px-2 py-1 w-32">SKU</th>
                                            <th className="text-left px-2 py-1">Producto</th>
                                            <th className="text-left px-2 py-1 w-16">HTTP</th>
                                            <th className="text-left px-2 py-1">Error</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {fallos.slice(0, 50).map((d, i) => (
                                            <tr key={i} className="border-t border-red-100">
                                              <td className="px-2 py-1">{d.codigo || d.sku || "—"}</td>
                                              <td className="px-2 py-1">{d.nombre_local || d.nombre_tn || "—"}</td>
                                              <td className="px-2 py-1">{d.http ?? "—"}</td>
                                              <td className="px-2 py-1 text-red-700 truncate max-w-xs">{d.error || "—"}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-[11px] text-neutral-600">Corrida sin fallos.</div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Detalle de una corrida de sync de PRECIOS. Muestra las variantes tocadas
 * con precio actual → precio nuevo, Δ, HTTP status y error si aplica.
 */
function PrecioRunDetail({ run, fallos }) {
  const detalles = run?.detalles || [];
  if (detalles.length === 0) {
    return <div className="text-[11px] text-neutral-600">Sin detalles guardados para esta corrida.</div>;
  }
  return (
    <div>
      {fallos.length > 0 && (
        <div className="text-[11px] font-bold text-red-700 mb-1">Con fallos: {fallos.length}</div>
      )}
      <div className="max-h-52 overflow-auto border border-sky-200 bg-white">
        <table className="w-full text-[11px]">
          <thead className="bg-sky-100 text-sky-900 sticky top-0">
            <tr>
              <th className="text-left px-2 py-1 w-20">SKU</th>
              <th className="text-left px-2 py-1">Producto TN</th>
              <th className="text-right px-2 py-1 w-24">Precio antes</th>
              <th className="text-right px-2 py-1 w-24">Precio nuevo</th>
              <th className="text-right px-2 py-1 w-16">HTTP</th>
              <th className="text-left px-2 py-1 w-40">Error</th>
            </tr>
          </thead>
          <tbody>
            {detalles.slice(0, 80).map((d, i) => (
              <tr key={i} className={`border-t border-sky-100 ${d.error ? "bg-red-50" : ""}`}>
                <td className="px-2 py-1 font-bold">{d.sku || "—"}</td>
                <td className="px-2 py-1 truncate max-w-xs">{d.nombre_tn || "—"}</td>
                <td className="px-2 py-1 text-right">$ {Number(d.precio_tn_actual || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</td>
                <td className="px-2 py-1 text-right font-bold">$ {Number(d.precio_nuevo || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</td>
                <td className="px-2 py-1 text-right">{d.http ?? "—"}</td>
                <td className="px-2 py-1 text-red-700 truncate max-w-xs">{d.error || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
